import { Controller } from "@hotwired/stimulus"

/*
  Controls the "+" button in the assistant form. Opens a dropup menu with a
  list of options on the left, and shows the sub-panel for the highlighted
  option on the right (cascading menu).

  Usage:

    <div data-controller="plus-menu">
      <button data-action="click->plus-menu#toggle" data-plus-menu-target="trigger">+</button>

      <div data-plus-menu-target="menu" hidden>
        <div data-plus-menu-target="itemList">
          <button data-action="mouseenter->plus-menu#hoverItem click->plus-menu#selectItem"
                  data-plus-menu-item-param="suggestions">…</button>
        </div>
        <div data-plus-menu-target="subPanel">
          <div data-plus-menu-target="subContent" data-key="suggestions">…</div>
        </div>
      </div>
    </div>
*/
export default class extends Controller {
  static targets = ["trigger", "menu", "item", "subContent"]
  static values = {
    activeItem: { type: String, default: "" },
    open: { type: Boolean, default: false },
    panelOpen: { type: Boolean, default: false },
    hoverDelay: { type: Number, default: 160 }
  }

  connect() {
    this.boundClickOutside = this._onClickOutside.bind(this)
    document.addEventListener("click", this.boundClickOutside)
    this._hoverTimer = null

    if (!this.activeItemValue && this.itemTargets.length > 0) {
      this.activeItemValue = this.itemTargets[0].dataset.plusMenuItemParam
    }
    this._renderActive()
  }

  disconnect() {
    document.removeEventListener("click", this.boundClickOutside)
    this._clearHoverTimer()
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    this.openValue = !this.openValue
  }

  close() {
    this.openValue = false
  }

  hoverItem(event) {
    const key = event.currentTarget.dataset.plusMenuItemParam
    if (!key || key === this.activeItemValue) return

    this._clearHoverTimer()
    this._hoverTimer = setTimeout(() => {
      this.activeItemValue = key
      this._hoverTimer = null
    }, this.hoverDelayValue)
  }

  leaveItem() {
    this._clearHoverTimer()
  }

  selectItem(event) {
    event.preventDefault()
    this._clearHoverTimer()
    const key = event.currentTarget.dataset.plusMenuItemParam
    if (key) this.activeItemValue = key
    this.panelOpenValue = true
  }

  back(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }
    this.panelOpenValue = false
  }

  _clearHoverTimer() {
    if (this._hoverTimer) {
      clearTimeout(this._hoverTimer)
      this._hoverTimer = null
    }
  }

  openValueChanged() {
    if (!this.hasMenuTarget) return
    if (this.openValue) {
      this.menuTarget.removeAttribute("hidden")
      this.menuTarget.classList.add("plus-menu--open")
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "true")
      }
    } else {
      this.menuTarget.setAttribute("hidden", "")
      this.menuTarget.classList.remove("plus-menu--open")
      this.panelOpenValue = false
      if (this.hasTriggerTarget) {
        this.triggerTarget.setAttribute("aria-expanded", "false")
      }
    }
  }

  panelOpenValueChanged() {
    if (!this.hasMenuTarget) return
    this.menuTarget.classList.toggle("plus-menu--panel-open", this.panelOpenValue)
  }

  activeItemValueChanged() {
    this._renderActive()
  }

  _renderActive() {
    this.itemTargets.forEach((item) => {
      const isActive = item.dataset.plusMenuItemParam === this.activeItemValue
      item.classList.toggle("plus-menu-item--active", isActive)
      item.setAttribute("aria-selected", isActive ? "true" : "false")
    })
    this.subContentTargets.forEach((panel) => {
      const isActive = panel.dataset.key === this.activeItemValue
      panel.toggleAttribute("hidden", !isActive)
    })
  }

  _onClickOutside(event) {
    if (!this.openValue) return
    const insideMenu = this.hasMenuTarget && this.menuTarget.contains(event.target)
    const insideTrigger = this.hasTriggerTarget && this.triggerTarget.contains(event.target)
    if (insideMenu || insideTrigger) return
    this.close()
  }
}
